import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  Autocomplete,
  Paper,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import { Refresh as RefreshIcon, Add as AddIcon, Delete as DeleteIcon, Schedule as ScheduleIcon, Save as SaveIcon, History as HistoryIcon, ExpandMore as ExpandMoreIcon, Search as SearchIcon, Print as PrintIcon, HowToReg as HowToRegIcon, Visibility as VisibilityIcon, LocalHospital as LocalHospitalIcon, PointOfSale as PointOfSaleIcon, HourglassEmpty as HourglassIcon } from '@mui/icons-material';
import { visitAPI, consultationAPI, medicineAPI, patientAPI, authAPI, settingsAPI } from '../services/api';
import PrintablePrescription from '../components/PrintablePrescription';
import PrintPortal from '../components/PrintPortal';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../constants/permissions';

const DoctorPortal = () => {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openTimeDialog, setOpenTimeDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [patientHistory, setPatientHistory] = useState(null);
  const [historyPatient, setHistoryPatient] = useState(null);
  const [selectedVisitForTime, setSelectedVisitForTime] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tabValue, setTabValue] = useState(0);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [openMedicineDialog, setOpenMedicineDialog] = useState(false);
  const [medicineForm, setMedicineForm] = useState({
    name: '',
    genericName: '',
    type: 'drops',
    strength: '',
    manufacturer: '',
    description: '',
  });
  const [allStaff, setAllStaff] = useState([]);
  const [referralOptions, setReferralOptions] = useState([]);
  const [clinicSettings, setClinicSettings] = useState(null);
  const [printConsultation, setPrintConsultation] = useState(null);

  useEffect(() => {
    settingsAPI.getSettings().then(res => setClinicSettings(res.data.data)).catch(() => {});
  }, []);

  const handlePrintPrescription = (consultation) => {
    setPrintConsultation(consultation);
    setTimeout(() => {
      window.print();
    }, 300);
  };
  
  const [consultationForm, setConsultationForm] = useState({
    chiefComplaint: '',
    historyOfPresentIllness: '',
    examination: {
      externalExam: '',
      slitLampExam: '',
      fundusExam: '',
      otherFindings: '',
    },
    diagnosis: {
      primary: '',
      secondary: [],
      icdCodes: [],
    },
    treatmentPlan: '',
    prescriptions: [],
    followUp: {
      required: false,
      afterDays: '',
      notes: '',
    },
    referTo: null,
    referralReason: '',
    notes: '',
  });

  useEffect(() => {
    fetchWaitingPatients();
    fetchMedicines();
    fetchAllPatients();
    fetchAllStaff();
  }, []);

  const fetchWaitingPatients = async () => {
    try {
      const response = await visitAPI.getVisits({ status: 'waiting-doctor' });
      setVisits(response.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch patients', 'error');
    }
  };

  const fetchMedicines = async () => {
    try {
      const response = await medicineAPI.getMedicines({ limit: 100 });
      setMedicines(response.data.data);
    } catch (error) {
      console.error('Failed to fetch medicines');
    }
  };

  const handleOpenMedicineDialog = () => {
    setMedicineForm({
      name: '',
      genericName: '',
      type: 'drops',
      strength: '',
      manufacturer: '',
      description: '',
    });
    setOpenMedicineDialog(true);
  };

  const handleSaveMedicine = async () => {
    if (!medicineForm.name.trim()) {
      showSnackbar('Please enter medicine name', 'error');
      return;
    }
    try {
      const response = await medicineAPI.createMedicine(medicineForm);
      setMedicines([...medicines, response.data.data]);
      showSnackbar('Medicine added successfully!');
      setOpenMedicineDialog(false);
      setMedicineForm({
        name: '',
        genericName: '',
        type: 'drops',
        strength: '',
        manufacturer: '',
        description: '',
      });
    } catch (error) {
      showSnackbar('Failed to add medicine', 'error');
    }
  };

  const fetchAllPatients = async () => {
    try {
      const response = await patientAPI.getPatients({ limit: 100 });
      setPatients(response.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch patients', 'error');
    }
  };

  const fetchAllStaff = async () => {
    try {
      // Create referral options with only departments
      const options = [
        { type: 'department', id: 'reception', name: 'Reception' },
        { type: 'department', id: 'optometrist', name: 'Optometrist' },
        { type: 'department', id: 'dispensing', name: 'OPD' },
      ];
      setReferralOptions(options);
    } catch (error) {
      console.error('Failed to fetch referral options');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleViewHistory = async (visit) => {
    try {
      const response = await patientAPI.getPatientHistory(visit.patient._id);
      setPatientHistory(response.data.data);
      setHistoryPatient(visit.patient);
      setOpenHistoryDialog(true);
    } catch (error) {
      showSnackbar('Failed to fetch patient history', 'error');
    }
  };

  const handleStartConsultation = async (visit) => {
    try {
      // Start the doctor stage and timer
      await visitAPI.startStage(visit._id, { stage: 'doctor' });
      setSelectedVisit(visit);
      setConsultationForm({
        ...consultationForm,
        chiefComplaint: visit.chiefComplaint || '',
      });
      setOpenDialog(true);
      showSnackbar('Timer started - Complete consultation to proceed', 'info');
      // Keep patient in the list - they're in-progress, not removed
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to start consultation', 'error');
    }
  };

  const handleAddPrescription = () => {
    setConsultationForm({
      ...consultationForm,
      prescriptions: [
        ...consultationForm.prescriptions,
        { medicine: null, dosage: '', frequency: '', duration: '', instructions: '' },
      ],
    });
  };

  const handleRemovePrescription = (index) => {
    const newPrescriptions = consultationForm.prescriptions.filter((_, i) => i !== index);
    setConsultationForm({ ...consultationForm, prescriptions: newPrescriptions });
  };

  const handleSaveConsultation = async () => {
    try {
      // Save consultation record
      await consultationAPI.createConsultation({
        visit: selectedVisit._id,
        ...consultationForm,
      });
      
      // End doctor stage (this calculates time spent)
      await visitAPI.endStage(selectedVisit._id, { notes: 'Consultation completed' });
      
      // Determine next stage based on referral or prescriptions
      let nextStatus, message;
      
      if (consultationForm.referTo) {
        // Referred to department or staff
        const referredOption = referralOptions.find(opt => opt.id === consultationForm.referTo);
        
        if (referredOption?.type === 'department') {
          // Referred to a department
          if (referredOption.id === 'reception') {
            nextStatus = 'waiting-reception';
          } else if (referredOption.id === 'optometrist') {
            nextStatus = 'waiting-optometrist';
          } else if (referredOption.id === 'dispensing') {
            nextStatus = 'waiting-dispensing';
          }
          message = `Patient referred to ${referredOption.name}`;
        } else {
          // Referred to specific staff member
          const staffMember = allStaff.find(s => s._id === consultationForm.referTo);
          if (staffMember?.role === 'doctor') {
            nextStatus = 'waiting-doctor';
          } else if (staffMember?.role === 'optometrist') {
            nextStatus = 'waiting-optometrist';
          } else if (staffMember?.role === 'reception') {
            nextStatus = 'waiting-reception';
          } else {
            nextStatus = 'waiting-dispensing';
          }
          message = `Patient referred to ${staffMember?.name || 'staff member'}`;
        }
        
        await visitAPI.updateVisit(selectedVisit._id, {
          status: nextStatus,
          referredTo: consultationForm.referTo,
          referralReason: consultationForm.referralReason
        });
      } else if (consultationForm.prescriptions.length > 0) {
        // Has prescriptions - go to dispensing
        nextStatus = 'waiting-dispensing';
        await visitAPI.updateVisit(selectedVisit._id, {
          status: nextStatus,
          referredTo: 'dispensing'
        });
        message = 'Consultation completed! Patient referred to dispensing.';
      } else {
        // No prescriptions, no referral - complete visit
        nextStatus = 'completed';
        await visitAPI.updateVisit(selectedVisit._id, {
          status: nextStatus,
          referredTo: null
        });
        message = 'Consultation completed! Visit marked as complete.';
      }
      
      showSnackbar(message, 'success');
      setOpenDialog(false);
      fetchWaitingPatients(); // Refresh list - patient removed after save
      resetForm();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save consultation', 'error');
    }
  };

  const handleCancelConsultation = async () => {
    try {
      // If user cancels without saving, end the stage to stop timer
      if (selectedVisit) {
        await visitAPI.endStage(selectedVisit._id, { notes: 'Consultation cancelled' });
        // Reset visit status back to waiting
        await visitAPI.updateVisit(selectedVisit._id, {
          status: 'waiting-doctor'
        });
      }
      setOpenDialog(false);
      resetForm();
      fetchWaitingPatients(); // Refresh to show patient back in waiting list
      showSnackbar('Consultation cancelled', 'info');
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to cancel', 'error');
    }
  };

  const resetForm = () => {
    setConsultationForm({
      chiefComplaint: '',
      historyOfPresentIllness: '',
      examination: {
        externalExam: '',
        slitLampExam: '',
        fundusExam: '',
        otherFindings: '',
      },
      diagnosis: {
        primary: '',
        secondary: [],
        icdCodes: [],
      },
      treatmentPlan: '',
      prescriptions: [],
      followUp: {
        required: false,
        afterDays: '',
        notes: '',
      },
      referTo: null,
      referralReason: '',
      notes: '',
    });
  };

  const filteredPatients = patients.filter(patient => 
    patient.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    patient.patientId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    patient.contact?.phone?.includes(searchQuery)
  );

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="700">
          Doctor Portal
        </Typography>
        <IconButton onClick={() => { fetchWaitingPatients(); fetchAllPatients(); }} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label={`Waiting Patients (${visits.length})`} />
        <Tab label="All Patients" />
      </Tabs>

      {tabValue === 0 && (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Token</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Age / Gender</TableCell>
                <TableCell>Chief Complaint</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No patients waiting for consultation</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visits.map((visit) => (
                  <TableRow key={visit._id} hover>
                    <TableCell>
                      <Chip
                        label={`#${visit.tokenNumber || '-'}`}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 800, fontSize: '0.9rem', minWidth: 44 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{visit.patient?.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {visit.patient?.patientId} • {visit.visitId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visit.patient?.age} yrs / {visit.patient?.gender}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{visit.chiefComplaint || 'N/A'}</Typography>
                      {visit.referralReason && (
                        <Typography variant="caption" color="primary.main" display="block">
                          Referred: {visit.referralReason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label="Waiting" color="warning" size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleStartConsultation(visit)}
                        sx={{ mr: 1, textTransform: 'none' }}
                      >
                        Start Consultation
                      </Button>
                      {hasPermission(user, 'patient_history') && (
                        <IconButton size="small" title="History" onClick={() => handleViewHistory(visit)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      )}
                      {hasPermission(user, 'timer') && (
                        <IconButton
                          size="small"
                          title="Times"
                          onClick={() => {
                            setSelectedVisitForTime(visit);
                            setOpenTimeDialog(true);
                          }}
                        >
                          <ScheduleIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      )}

      {tabValue === 1 && (
        <Box>
          <TextField
            fullWidth
            placeholder="Search by name, patient ID, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Grid container spacing={3}>
            {filteredPatients.map((patient) => (
              <Grid item xs={12} md={6} lg={4} key={patient._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          {patient.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {patient.patientId}
                        </Typography>
                      </Box>
                      <Chip label="Patient" color="info" size="small" />
                    </Box>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Age:</strong> {patient.age} • <strong>Gender:</strong> {patient.gender}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Phone:</strong> {patient.contact?.phone || 'N/A'}
                    </Typography>

                    {hasPermission(user, 'patient_history') && (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={() => handleViewHistory({ patient })}
                      >
                        View History
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Consultation Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCancelConsultation}
        maxWidth="lg" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h5" fontWeight="bold">
            Consultation - {selectedVisit?.patient?.name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Chief Complaint"
                value={consultationForm.chiefComplaint}
                onChange={(e) => setConsultationForm({ ...consultationForm, chiefComplaint: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="History of Present Illness"
                multiline
                rows={2}
                value={consultationForm.historyOfPresentIllness}
                onChange={(e) => setConsultationForm({ ...consultationForm, historyOfPresentIllness: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Examination
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="External Exam"
                multiline
                rows={2}
                value={consultationForm.examination.externalExam}
                onChange={(e) => setConsultationForm({
                  ...consultationForm,
                  examination: { ...consultationForm.examination, externalExam: e.target.value }
                })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Slit Lamp Exam"
                multiline
                rows={2}
                value={consultationForm.examination.slitLampExam}
                onChange={(e) => setConsultationForm({
                  ...consultationForm,
                  examination: { ...consultationForm.examination, slitLampExam: e.target.value }
                })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fundus Exam"
                multiline
                rows={2}
                value={consultationForm.examination.fundusExam}
                onChange={(e) => setConsultationForm({
                  ...consultationForm,
                  examination: { ...consultationForm.examination, fundusExam: e.target.value }
                })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Primary Diagnosis"
                required
                value={consultationForm.diagnosis.primary}
                onChange={(e) => setConsultationForm({
                  ...consultationForm,
                  diagnosis: { ...consultationForm.diagnosis, primary: e.target.value }
                })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Treatment Plan"
                required
                multiline
                rows={3}
                value={consultationForm.treatmentPlan}
                onChange={(e) => setConsultationForm({ ...consultationForm, treatmentPlan: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Prescriptions
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddPrescription}
                  variant="contained"
                  color="primary"
                >
                  Add Prescription
                </Button>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleOpenMedicineDialog}
                  variant="outlined"
                  color="success"
                >
                  Add New Medicine
                </Button>
              </Box>
              {consultationForm.prescriptions.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Medicine</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Dosage</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Frequency</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Duration</TableCell>
                      <TableCell width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consultationForm.prescriptions.map((prescription, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Autocomplete
                            size="small"
                            options={medicines}
                            getOptionLabel={(option) => option.name}
                            value={medicines.find(m => m._id === prescription.medicine) || null}
                            onChange={(e, newValue) => {
                              const newPrescriptions = [...consultationForm.prescriptions];
                              newPrescriptions[index].medicine = newValue?._id;
                              setConsultationForm({ ...consultationForm, prescriptions: newPrescriptions });
                            }}
                            renderInput={(params) => (
                              <TextField 
                                {...params} 
                                placeholder="Select or search medicine..."
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                      borderColor: 'primary.main',
                                    },
                                  },
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}>
                                <Typography variant="body2" fontWeight="bold">{option.name}</Typography>
                                {option.genericName && option.genericName !== option.name && (
                                  <Typography variant="caption" color="text.secondary">
                                    Generic: {option.genericName}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            ListboxProps={{
                              style: { maxHeight: '250px' }
                            }}
                            filterOptions={(options, params) => {
                              const filtered = options.filter(option =>
                                option.name.toLowerCase().includes(params.inputValue.toLowerCase()) ||
                                option.genericName?.toLowerCase().includes(params.inputValue.toLowerCase())
                              );
                              return filtered;
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="1 drop"
                            value={prescription.dosage}
                            onChange={(e) => {
                              const newPrescriptions = [...consultationForm.prescriptions];
                              newPrescriptions[index].dosage = e.target.value;
                              setConsultationForm({ ...consultationForm, prescriptions: newPrescriptions });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="3x daily"
                            value={prescription.frequency}
                            onChange={(e) => {
                              const newPrescriptions = [...consultationForm.prescriptions];
                              newPrescriptions[index].frequency = e.target.value;
                              setConsultationForm({ ...consultationForm, prescriptions: newPrescriptions });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="7 days"
                            value={prescription.duration}
                            onChange={(e) => {
                              const newPrescriptions = [...consultationForm.prescriptions];
                              newPrescriptions[index].duration = e.target.value;
                              setConsultationForm({ ...consultationForm, prescriptions: newPrescriptions });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleRemovePrescription(index)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Referral (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={referralOptions}
                getOptionLabel={(option) => option.name}
                value={referralOptions.find(opt => opt.id === consultationForm.referTo) || null}
                onChange={(e, newValue) => {
                  setConsultationForm({ ...consultationForm, referTo: newValue?.id || null });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Refer To"
                    placeholder="Select department..."
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Referral Reason"
                value={consultationForm.referralReason}
                onChange={(e) => setConsultationForm({ ...consultationForm, referralReason: e.target.value })}
                disabled={!consultationForm.referTo}
                placeholder="Reason for referral..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={2}
                value={consultationForm.notes}
                onChange={(e) => setConsultationForm({ ...consultationForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={handleCancelConsultation}
            size="large"
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveConsultation} 
            variant="contained"
            size="large"
            color="success"
            startIcon={<SaveIcon />}
          >
            Save & Complete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Time Details Dialog */}
      <Dialog 
        open={openTimeDialog} 
        onClose={() => {
          setOpenTimeDialog(false);
          setSelectedVisitForTime(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Patient Journey Timeline
            </Typography>
          </Box>
          {selectedVisitForTime && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedVisitForTime.patient?.name} - {selectedVisitForTime.visitId}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedVisitForTime && (
            <Box sx={{ py: 2 }}>
              {(() => {
                const stageConfig = {
                  reception: { color: '#7FAFD1', icon: <HowToRegIcon fontSize="small" />, label: 'Reception' },
                  optometrist: { color: '#7C84D4', icon: <VisibilityIcon fontSize="small" />, label: 'Optometrist' },
                  doctor: { color: '#8B90A7', icon: <LocalHospitalIcon fontSize="small" />, label: 'Doctor' },
                  opd: { color: '#6BBD9E', icon: <PointOfSaleIcon fontSize="small" />, label: 'OPD' },
                };
                const stages = selectedVisitForTime.stages || [];

                return (
                  <Box>
                    {stages.map((stage, index) => {
                      const nextStage = stages[index + 1];
                      const waitTime = stage.endTime && nextStage?.startTime
                        ? Math.floor((new Date(nextStage.startTime) - new Date(stage.endTime)) / 1000)
                        : stage.endTime && selectedVisitForTime.status.includes('waiting')
                        ? Math.floor((new Date() - new Date(stage.endTime)) / 1000)
                        : null;

                      const config = stageConfig[stage.stage] || { color: '#9B9AB0', icon: null, label: stage.stage };
                      const isLast = index === stages.length - 1;

                      return (
                        <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                          {/* Timeline rail */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 40, flexShrink: 0 }}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: `${config.color}22`, color: config.color }}>
                              {config.icon}
                            </Avatar>
                            {!isLast && (
                              <Box sx={{ width: 2, flexGrow: 1, my: 0.5, bgcolor: '#E9E9F2', minHeight: 24 }} />
                            )}
                          </Box>

                          {/* Stage content */}
                          <Box sx={{ flex: 1, pb: isLast ? 0 : 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {config.label}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  Stage {index + 1} of {stages.length}
                                </Typography>
                              </Typography>
                              <Chip
                                size="small"
                                label={stage.duration ? `${Math.floor(stage.duration / 60)}m ${stage.duration % 60}s` : 'In Progress'}
                                color={stage.duration ? 'success' : 'warning'}
                                variant={stage.duration ? 'filled' : 'outlined'}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                              <Typography variant="body2" color="text.secondary">
                                <Box component="span" sx={{ color: '#9B9AB0', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', mr: 0.5 }}>Start</Box>
                                {stage.startTime ? new Date(stage.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                <Box component="span" sx={{ color: '#9B9AB0', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', mr: 0.5 }}>End</Box>
                                {stage.endTime ? new Date(stage.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Ongoing'}
                              </Typography>
                            </Box>
                            {stage.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {stage.notes}
                              </Typography>
                            )}

                            {/* Waiting connector */}
                            {waitTime !== null && (
                              <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1, mt: 1.5,
                                px: 1.5, py: 1, borderRadius: 1.5,
                                bgcolor: '#F7F0E2', border: '1px dashed #DCB26E',
                              }}>
                                <HourglassIcon sx={{ fontSize: 14, color: '#C89D55' }} />
                                <Typography variant="caption" fontWeight={600} color="#C89D55">
                                  Waited {Math.floor(waitTime / 60)}m {waitTime % 60}s
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {nextStage ? `before ${stageConfig[nextStage.stage]?.label || nextStage.stage}` : '— currently waiting'}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}

                    {/* Journey summary */}
                    <Paper variant="outlined" sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: '#FAFAFD', display: 'flex', gap: 4 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Total Active Time
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {(() => {
                            const totalActive = stages.reduce((sum, s) => sum + (s.duration || 0), 0);
                            return `${Math.floor(totalActive / 60)}m ${totalActive % 60}s`;
                          })()}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Current Status
                        </Typography>
                        <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                          {selectedVisitForTime.status.replace(/-/g, ' ')}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenTimeDialog(false);
            setSelectedVisitForTime(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Patient History Dialog */}
      <Dialog
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Patient History - {patientHistory?.consultations?.[0]?.patient?.name || patientHistory?.optometryRecords?.[0]?.patient?.name || 'N/A'}
        </DialogTitle>
        <DialogContent>
          {patientHistory && (
            <Box>
              {/* Previous Consultations */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Previous Consultations</Typography>
              {patientHistory.consultations?.length > 0 ? (
                patientHistory.consultations.map((consultation) => (
                  <Accordion key={consultation._id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 1 }}>
                        <Typography>
                          Visit {consultation.visit?.visitId} - {new Date(consultation.visit?.visitDate).toLocaleDateString()}
                          {consultation.doctor?.name && ` - Dr. ${consultation.doctor.name}`}
                        </Typography>
                        {hasPermission(user, 'printing') && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PrintIcon />}
                            onClick={(e) => { e.stopPropagation(); handlePrintPrescription(consultation); }}
                          >
                            Print Rx
                          </Button>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell><strong>Chief Complaint</strong></TableCell>
                            <TableCell>{consultation.chiefComplaint || 'N/A'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Diagnosis</strong></TableCell>
                            <TableCell>{consultation.diagnosis?.primary || 'N/A'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Treatment Plan</strong></TableCell>
                            <TableCell>{consultation.treatmentPlan || 'N/A'}</TableCell>
                          </TableRow>
                          {consultation.prescriptions?.length > 0 && (
                            <TableRow>
                              <TableCell><strong>Prescriptions</strong></TableCell>
                              <TableCell>
                                {consultation.prescriptions.map((p, i) => (
                                  <div key={i}>
                                    {p.medicine?.name || 'N/A'} - {p.dosage}, {p.frequency}, {p.duration}
                                  </div>
                                ))}
                              </TableCell>
                            </TableRow>
                          )}
                          {consultation.followUp?.required && (
                            <TableRow>
                              <TableCell><strong>Follow-up</strong></TableCell>
                              <TableCell>After {consultation.followUp.afterDays} days - {consultation.followUp.notes}</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography color="text.secondary">No previous consultations</Typography>
              )}

              {/* Optometry Records */}
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Previous Optometry Records</Typography>
              {patientHistory.optometryRecords?.length > 0 ? (
                patientHistory.optometryRecords.map((record) => (
                  <Accordion key={record._id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        Visit {record.visit?.visitId} - {new Date(record.visit?.visitDate).toLocaleDateString()} 
                        {record.performedBy?.name && ` - By ${record.performedBy.name}`}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell><strong>Visual Acuity (Right)</strong></TableCell>
                            <TableCell>UCVA: {record.visualAcuity?.right?.ucva || 'N/A'}, VACGL: {record.visualAcuity?.right?.vacgl || 'N/A'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Visual Acuity (Left)</strong></TableCell>
                            <TableCell>UCVA: {record.visualAcuity?.left?.ucva || 'N/A'}, VACGL: {record.visualAcuity?.left?.vacgl || 'N/A'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Recommendations</strong></TableCell>
                            <TableCell>{record.recommendations || 'None'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Remarks</strong></TableCell>
                            <TableCell>{record.optometristRemarks || 'None'}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography color="text.secondary">No previous optometry records</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Medicine Dialog */}
      <Dialog 
        open={openMedicineDialog} 
        onClose={() => setOpenMedicineDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Add New Medicine
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Medicine Name"
                required
                value={medicineForm.name}
                onChange={(e) => setMedicineForm({ ...medicineForm, name: e.target.value })}
                placeholder="e.g., Timolol Eye Drops"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Generic Name"
                value={medicineForm.genericName}
                onChange={(e) => setMedicineForm({ ...medicineForm, genericName: e.target.value })}
                placeholder="e.g., Timolol Maleate"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Type"
                required
                value={medicineForm.type}
                onChange={(e) => setMedicineForm({ ...medicineForm, type: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="drops">Drops</option>
                <option value="tablet">Tablet</option>
                <option value="capsule">Capsule</option>
                <option value="syrup">Syrup</option>
                <option value="ointment">Ointment</option>
                <option value="injection">Injection</option>
                <option value="other">Other</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Strength"
                placeholder="e.g., 0.5%, 10mg"
                value={medicineForm.strength}
                onChange={(e) => setMedicineForm({ ...medicineForm, strength: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={medicineForm.manufacturer}
                onChange={(e) => setMedicineForm({ ...medicineForm, manufacturer: e.target.value })}
                placeholder="e.g., Sun Pharma, Cipla"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={medicineForm.description}
                onChange={(e) => setMedicineForm({ ...medicineForm, description: e.target.value })}
                placeholder="Additional information about the medicine..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenMedicineDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveMedicine}
            variant="contained"
            disabled={!medicineForm.name.trim()}
          >
            Add Medicine
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Rendered via portal directly into document.body so it survives the .no-print layout */}
      <PrintPortal>
        {printConsultation && (
          <PrintablePrescription
            clinic={clinicSettings}
            doctor={printConsultation.doctor}
            patient={historyPatient}
            visit={printConsultation.visit}
            consultation={printConsultation}
          />
        )}
      </PrintPortal>
    </Box>
  );
};

export default DoctorPortal;
